/**
 * @module Map
 */
import {default as OlMap, MapOptions} from 'ol/Map';
import GIS from "./app"
import {fromExtent} from 'ol/geom/Polygon';
import {
    toLonLat as olToLonLat,
    fromLonLat as olFromLonLat,
    ProjectionLike
} from 'ol/proj';
import {transformExtent, transform as olTransform} from 'ol/proj';
import {
    DragPan,
    DoubleClickZoom,
    DragZoom,
    KeyboardPan,
    KeyboardZoom,
    MouseWheelZoom,
    PinchZoom,
    PinchRotate,
    DragRotate,
    DragAndDrop,
    Select,
    Draw,
    defaults as interactionDefaults, Interaction
} from "ol/interaction";
import {defaults as controlDefaults} from "ol/control";
import Identify from "./interaction/identify";
import Hover from "./interaction/hover";
import WKT from "ol/format/WKT";
import GeoJSON from "ol/format/GeoJSON";
import html2canvas from "html2canvas";
import {MapConfiguration} from "./configuration/map";
import {Coordinate} from "ol/coordinate";
import {
    Transform,
    Renderer,
    MapEvent,
    GeometryString,
    Blob,
    XY,
    GetExtent,
    Rotation,
    Zoom,
    ScaleOrPointResolutionCallback,
    BoundaryCallback
} from "./general/callback";
import {EPSG, ProjectionTransform, Listener, Render, Export, ExtentOptions, ZoomOptions, BoundaryCallbackOptions} from "./general/options"
import { createEmpty, extendCoordinate, Extent } from 'ol/extent';
import {platformModifierKeyOnly} from "ol/events/condition";

/**
 * Callback types enumeration.
 * The event type or array of event types. map.on('') or map.once('') {@link https://openlayers.org/en/latest/apidoc/module-ol_Map-Map.html}
 */
type CallbackType = 'contextmenu' | 'rotationDegreesAndRadians' | 'changeCenter' | 'loading' | 'move' | 'moveend' | 'postrender' | 'boundary';

/**
 * From Longitude and Latitude.
 * See {@link https://openlayers.org/en/latest/apidoc/module-ol_proj.html#.fromLonLat}
 * @param coordinate Lon,Lat coordinates as real numbers.
 * @param projection EPSG code as a string e.g. EPSG:3857.
 * @param callback
 * @throws Transformation failed, might be not valid CRS or coordinates.
 * @returns Transformed coordinates array [X,Y]
 */
export function fromLonLat(coordinate: Coordinate, projection: ProjectionLike, callback?: Transform) {
    if (!projection || (typeof projection === "string" && !projection.startsWith('EPSG:'))) {
        projection = 'EPSG:3857'
    }

    let transformed;
    try {
        transformed = olFromLonLat(coordinate, projection);
    } catch (e: unknown) {
        if (e instanceof Error) {
            console && console.warn(e.message);
        }
        throw Error('Transformation failed, might be not valid CRS or coordinates.')
    }

    if (callback) {
        callback(transformed[0], transformed[1]);
    }

    return [transformed[0], transformed[1]];
}

/**
 * To Longitude and Latitude.
 * See {@link https://openlayers.org/en/latest/apidoc/module-ol_proj.html#.toLonLat}
 * @param coordinate X,Y coordinates as real numbers.
 * @param projection EPSG code as a string e.g. EPSG:3857.
 * @param callback
 * @throws Transformation failed, might be not valid CRS or coordinates.
 * @returns Transformed coordinates array [Lon,Lat]
 */
export function toLonLat(coordinate: Coordinate, projection: ProjectionLike, callback?: Transform | null) {
    if (!projection || (typeof projection === "string" && !projection.startsWith('EPSG:'))) {
        projection = 'EPSG:3857'
    }

    let transformed;
    try {
        transformed = olToLonLat(coordinate, projection);
    } catch (e: unknown) {
        if (e instanceof Error) {
            console && console.warn(e.message);
        }
        throw Error('Transformation failed, might be not valid CRS or coordinates.')
    }

    if (callback) {
        callback(transformed[0], transformed[1]);
    }

    return [transformed[0], transformed[1]];
}
/**
 * Calculate extent from coordinates of Point, LineString, or Polygon geometry.
 * Supports:
 * - Point: [x, y]
 * - LineString: [[x1, y1], [x2, y2], ...]
 * - Polygon: [[[x1, y1], [x2, y2], ...]] (only outer ring is considered)
 *
 * @param coordinates The coordinates array
 * @returns A valid [minx, miny, maxx, maxy] extent
 * @throws If coordinates are empty or invalid
 */
export function getExtentFromCoordinates(
    coordinates: number[] | number[][] | number[][][]
): Extent {
    if (!coordinates || !Array.isArray(coordinates)) {
        throw new Error('Coordinates must be a non-empty array.');
    }

    const extent: Extent = createEmpty();

    const flatten = (coords: number[] | number[][] | number[][][]) => {
        if (typeof coords[0] === 'number') {
            // Point: [x, y]
            return [coords as [number, number]];
        } else if (Array.isArray(coords[0]) && typeof coords[0][0] === 'number') {
            // LineString: [[x1, y1], [x2, y2], ...]
            return coords as [number, number][];
        } else if (
            Array.isArray(coords[0]) &&
            Array.isArray(coords[0][0]) &&
            typeof coords[0][0][0] === 'number'
        ) {
            // Polygon: [[[x1, y1], [x2, y2], ...]]
            return coords[0] as [number, number][];
        } else {
            throw new Error('Invalid coordinate structure.');
        }
    };

    const flatCoords = flatten(coordinates);

    flatCoords.forEach((coord) => {
        if (!Array.isArray(coord) || coord.length < 2) {
            throw new Error('Invalid coordinate entry.');
        }
        extendCoordinate(extent, coord as [number, number]);
    });

    return extent;
}

/**
 * Transform from source to destination projection.
 * See {@link https://openlayers.org/en/latest/apidoc/module-ol_proj.html#.transform}
 * @param coordinate Lon,Lat or Lon,Lat coordinates as real numbers.
 * @param source EPSG code as a string e.g. EPSG:3857.
 * @param destination EPSG code as a string e.g. EPSG:3857.
 * @param callback
 * @throws Transformation failed, might be not valid CRS or coordinates.
 * @returns Transformed coordinates array [X,Y]
 */
export function transform(coordinate: Coordinate, source: ProjectionLike, destination: ProjectionLike, callback?: Transform | null) {
    if (!source || (typeof source === "string" && !source.startsWith('EPSG:'))) {
        console && console.warn('Source destination has not been defined. EPSG:3857 is going to be used.');
        source = 'EPSG:3857'
    }

    if (!destination || (typeof destination === "string" && !destination.startsWith('EPSG:'))) {
        throw Error('Destination coordinate system is mandatory.')
    }

    let transformed;
    try {
        transformed = olTransform(coordinate, source, destination);
    } catch (e: unknown) {
        if (e instanceof Error) {
            console && console.warn(e.message);
        }
        throw Error('Transformation failed, might be not valid CRS or coordinates.')
    }

    if (callback) {
        callback(transformed[0], transformed[1]);
    }

    return [transformed[0], transformed[1]];
}

class Map {
    public olObject!: OlMap | undefined;
    private readonly GIS;
    public listeners: Listener = {};
    public captureListeners: Listener = {};

    constructor(GIS: GIS) {
        this.GIS = GIS;
    }
    /**
     * Initialization of the Map object.
     * @param mapConfiguration - Map configuration
     */
    public init(mapConfiguration: MapConfiguration) {

        const baseInteractions = interactionDefaults({
            altShiftDragRotate: mapConfiguration.defaultInteractions.altShiftDragRotate,
            pinchRotate: mapConfiguration.defaultInteractions.pinchRotate,
            doubleClickZoom: mapConfiguration.defaultInteractions.doubleClickZoom,
            keyboard: mapConfiguration.defaultInteractions.keyboard,
            mouseWheelZoom: mapConfiguration.defaultInteractions.mouseWheelZoom,
            shiftDragZoom: mapConfiguration.defaultInteractions.shiftDragZoom,
            dragPan: mapConfiguration.defaultInteractions.dragPan,
            pinchZoom: mapConfiguration.defaultInteractions.pinchZoom
        });

        const mouseWheelZoomConfig = mapConfiguration.interactions?.find(
            (interactionOption) => interactionOption.name === 'MouseWheelZoom'
        );

        if (mouseWheelZoomConfig) {
            baseInteractions.forEach((interaction) => {
                if (interaction instanceof MouseWheelZoom) {
                    baseInteractions.remove(interaction);
                }
            });

            if (mouseWheelZoomConfig.condition === 'ctrlKeyOnly') {
                baseInteractions.extend([
                    new MouseWheelZoom({
                        condition: platformModifierKeyOnly
                    })
                ]);
            } else {
                baseInteractions.extend([
                    new MouseWheelZoom()
                ]);
            }
        }

        const options: MapOptions = {
            target: mapConfiguration.target,
            view: this.GIS.View.olObject,
            layers: this.GIS.Layer.groupEntries,
            controls: controlDefaults({
                zoom: false,
                rotate: false,
                attribution: false,
            }),
            interactions: baseInteractions,
        };

        if (mapConfiguration.pixelRatio) {
            options['pixelRatio'] = mapConfiguration.pixelRatio;
        }

        if (mapConfiguration.moveTolerance) {
            options['moveTolerance'] = mapConfiguration.moveTolerance;
        }

        this.olObject = new OlMap(options);

        this.GIS.Interaction.entries.map((interaction: Interaction) => {
            this.olObject!.addInteraction(interaction);
        })

        if (mapConfiguration.overlay) {
            this.GIS.Overlay.init(mapConfiguration.overlay)
        }

        if (mapConfiguration.edit) {
            this.GIS.Edit.init(mapConfiguration.edit)
        }
    }
    /**
     * @deprecated since version 1.1 - use {@link GIS.View.getRotation} instead
     * This method is deprecated and will be removed in future releases.
     */
    public getRotation(callback?:Rotation) {
        console.warn('GIS.Map.getRotation is deprecated. Use GIS.View.getRotation instead. This method will be removed in future releases.');
        return this.GIS.View.getRotation(callback);
    }
    /**
     * @deprecated since version 1.1 - use {@link GIS.View.zoomToExtent} instead
     * This method is deprecated and will be removed in future releases.
     */
    public zoomToExtent(extent: import("ol/extent.js").Extent, maxZoom?: number | undefined, options?: ZoomOptions) {
        console.warn('GIS.Map.zoomToExtent is deprecated. Use GIS.View.zoomToExtent instead. This method will be removed in future releases.');
        return this.GIS.View.zoomToExtent(extent, maxZoom, options);
    }
    /**
     * @deprecated since version 1.1 - use {@link GIS.View.getExtent} instead
     * This method is deprecated and will be removed in future releases.
     */
    public getViewExtent (callback?: GetExtent, epsg?: number, options?: ExtentOptions) {
        console.warn('GIS.Map.getViewExtent is deprecated. Use GIS.View.getExtent instead. This method will be removed in future releases.');
        return this.GIS.View.getExtent(callback, epsg, options);
    }
    /**
     * @deprecated since version 1.1 - use {@link GIS.View.rotate} instead
     * This method is deprecated and will be removed in future releases.
     * @param degrees
     * @example Map.rotate(35.1)
     */
    public rotate(degrees:number) {
        console.warn('GIS.Map.rotate is deprecated. Use GIS.View.rotate instead. This method will be removed in future releases.');
        this.GIS.View.rotate(degrees);
    }
    /**
     * @deprecated since version 1.1 - use {@link GIS.View.getCenter} instead
     * This method is deprecated and will be removed in future releases.
     */
    public getCenter(callback?: XY) {
        console.warn('GIS.Map.getCenter is deprecated. Use GIS.View.getCenter instead. This method will be removed in future releases.');
        return this.GIS.View.getCenter(callback);
    }

    /**
     * @deprecated since version 1.1 - use {@link GIS.View.getZoom} instead
     * This method is deprecated and will be removed in future releases.
     */
    public getZoom(callback?:Zoom | null) {
        console.warn('GIS.Map.getZoom is deprecated. Use GIS.View.getZoom instead. This method will be removed in future releases.');
        return this.GIS.View.getZoom(callback);
    }

    /**
     * @deprecated since version 1.1 - use {@link GIS.View.setZoom} instead
     * This method is deprecated and will be removed in future releases.
     */
    public setZoom (zoom: number) {
        console.warn('GIS.Map.setZoom is deprecated. Use GIS.View.setZoom instead. This method will be removed in future releases.');
        return this.GIS.View.setZoom(zoom);
    }

    /**
     * @deprecated since version 1.1 - use {@link GIS.View.getPointResolution} instead
     * This method is deprecated and will be removed in future releases.
     */
    public getPointResolution(callback?:ScaleOrPointResolutionCallback, epsg?: number) {
        console.warn('GIS.Map.getPointResolution is deprecated. Use GIS.View.getPointResolution instead. This method will be removed in future releases.');
        return this.GIS.View.getPointResolution(callback, epsg);
    }

    /**
     * @deprecated since version 1.1 - use {@link GIS.View.getScale} instead
     * This method is deprecated and will be removed in future releases.
     */
    public getScale(callback?:ScaleOrPointResolutionCallback) {
        console.warn('GIS.Map.getScale is deprecated. Use GIS.View.getScale instead. This method will be removed in future releases.');
        this.GIS.View.getScale(callback);
    }

    /**
     * To Longitude and Latitude.
     * See {@link https://openlayers.org/en/latest/apidoc/module-ol_proj.html#.toLonLat}
     * @param coordinate X,Y coordinates as real numbers.
     * @param projection EPSG code as a string e.g. EPSG:3857.
     * @param callback
     * @throws Transformation failed, might be not valid CRS or coordinates.
     * @returns Transformed coordinates array [Lon,Lat]
     */
    public toLonLat(coordinate: Coordinate, projection: ProjectionLike, callback?: Transform | null) {
        return toLonLat(coordinate, projection, callback);
    }

    /**
     * From Longitude and Latitude.
     * See {@link https://openlayers.org/en/latest/apidoc/module-ol_proj.html#.fromLonLat}
     * @param coordinate Lon,Lat coordinates as real numbers.
     * @param projection EPSG code as a string e.g. EPSG:3857.
     * @param callback
     * @throws Transformation failed, might be not valid CRS or coordinates.
     * @returns Transformed coordinates array [X,Y]
     */
    public fromLonLat(coordinate: Coordinate, projection: ProjectionLike, callback?: Transform) {
        return fromLonLat(coordinate, projection, callback);
    }

    /**
     * Transform from source to destination projection.
     * See {@link https://openlayers.org/en/latest/apidoc/module-ol_proj.html#.transform}
     * @param coordinate Lon,Lat or Lon,Lat coordinates as real numbers.
     * @param source EPSG code as a string e.g. EPSG:3857.
     * @param destination EPSG code as a string e.g. EPSG:3857.
     * @param callback
     * @throws Transformation failed, might be not valid CRS or coordinates.
     * @returns Transformed coordinates array [X,Y]
     */
    public transform(coordinate: Coordinate, source: ProjectionLike, destination: ProjectionLike, callback?: Transform | null) {
        return transform(coordinate, source, destination, callback);
    }
    /**
     * Set callback for fires e.g. change:center, change:resolution {@link https://openlayers.org/en/latest/apidoc/module-ol_View-View.html}
     * Callback can be deactivated when you provide null value for callback parameter
     * @param name - Trigger name, already supported 'rotationDegreesAndRadians', 'changeCenter' and 'loading'.
     *   Supported values are:
     *   - 'rotationDegreesAndRadians': called when the rotation angle of the map changes, with the new angle in degrees and radians as arguments.
     *   - 'changeCenter': called when the center of the map changes, with the new center coordinates in the map projection as arguments.
     *   - 'loadingEnd': called when all tiles in the map have finished loading. It is based on center, rotation and resolution changes.
     *   - 'move': called when at least one: center, rotation, resolution of the map changes, with the new center coordinates in the map projection as arguments.
     *   - 'contextmenu': prevent default right click menu and return X,Y click coordinate in pixels within map window.
     *   - 'moveend': called when the map has completed moving, with the new center coordinates in the map projection as arguments.
     *   - 'postrender': called after the map has been rendered, with the new center coordinates in the map projection as arguments.
     *   - 'boundary': called when the tracked point crosses the edge of a boundary layer (inside/outside state changes). By
     *     default the tracked point is the cursor (updated on pointer move); pass `track: 'center'` to instead follow the center
     *     of the map window (updated on pan/zoom). The boundary polygon lives in a normal vector layer (e.g. the Swedish
     *     territory for MDP); use this to swap coordinate tooltip labels the moment the point enters or leaves that area. Fires
     *     only on transitions, with `{ inside, coordinate }`. `options` must carry `layerName`, and may carry `track`
     *     (`'cursor'` | `'center'`, default `'cursor'`), `epsg` (projection of the reported coordinate) and `throttle` (min ms
     *     between point-in-boundary checks, default 50). It is the live counterpart of {@link Analysis.isCoordinateInsideLayer}.
     * @param options - Options bag for the callback. `epsg` for coordinate-carrying triggers; for `'boundary'` a
     *   {@link BoundaryCallbackOptions} with `layerName` (required), `track`, `epsg` and `throttle`.
     * @param callback - Callback response based on types of triggers. Pass `null` to deactivate a previously set callback.
     * @throws No such layer in the map. (boundary, when `options.layerName` is missing or unknown)
     *
     * @example
     * // Follow the cursor (default)
     * GIS.Map.setCallback('boundary', { layerName: 'sweden_boundary', epsg: 3006 }, ({ inside, coordinate }) => {
     *     tooltip.setLabelFormat(inside ? 'SWEREF' : 'WGS84');
     * });
     * // Follow the map-window center instead
     * GIS.Map.setCallback('boundary', { layerName: 'sweden_boundary', track: 'center' }, ({ inside }) => {
     *     header.setRegion(inside ? 'Sweden' : null);
     * });
     * // Deactivate
     * GIS.Map.setCallback('boundary', { layerName: 'sweden_boundary' }, null);
     */
    public setCallback(name: 'boundary', options: BoundaryCallbackOptions, callback?: BoundaryCallback | null): void;
    public setCallback(name: Exclude<CallbackType, 'boundary'>, options?: EPSG, callback?: MapEvent | null): void;
    public setCallback(name: CallbackType, options?: any, callback?: any): void {
        switch (name) {
            case 'contextmenu':
                const viewPort = this.olObject!.getViewport();
                if (this.listeners[name] != undefined && callback === null) {
                    viewPort.removeEventListener('contextmenu', this.listeners[name])
                    delete this.listeners[name]

                    if (this.captureListeners[name]) {
                        viewPort.removeEventListener('pointerdown', this.captureListeners[name], true );
                        delete this.captureListeners[name];
                    }
                    return
                }

                const pointerDownCapture = (event: PointerEvent) => {
                    try {
                        if (
                            event.button === 2 &&
                            this.GIS.Edit.isEditing()
                        ) {
                            event.preventDefault();
                            event.stopPropagation();
                            event.stopImmediatePropagation();
                        }
                    } catch (e) {
                        console.error('Common map - pointerdown handler error', e);
                    }
                };

                viewPort.addEventListener('pointerdown', pointerDownCapture, true);
                this.captureListeners[name] = pointerDownCapture;

                const contextMenuListener = (event: MouseEvent) => {
                    event.preventDefault();
                    let coordinates = this.olObject!.getEventCoordinate(event);
                    if (options && options.epsg) {
                        coordinates = transform(coordinates, this.GIS.View.olObject.getProjection().getCode(), options.epsg);
                    }
                    callback?.({
                        clientX: event.clientX,
                        clientY: event.clientY,
                        X: coordinates[0],
                        Y: coordinates[1]
                    });
                };

                this.listeners[name] = contextMenuListener;
                viewPort.addEventListener('contextmenu', this.listeners[name])

                break;
            case 'rotationDegreesAndRadians':
                if (this.GIS.View.listeners[name] && callback === null) {
                    this.GIS.View.olObject.un('change:rotation', this.GIS.View.listeners[name])
                    delete this.GIS.View.listeners[name]
                    return
                }
                const rotationListener = () => {
                    this.GIS.View.getRotation((degrees, radians) => {
                        callback?.(degrees, radians);
                    });
                };
                this.GIS.View.listeners[name] = rotationListener;
                this.GIS.View.olObject.on('change:rotation', this.GIS.View.listeners[name])
                break;
            case 'changeCenter':
                if (this.GIS.View.listeners[name] && callback === null) {
                    this.GIS.View.olObject.un('change:center', this.GIS.View.listeners[name])
                    delete this.GIS.View.listeners[name]
                    return
                }
                const changeCenterListener = () => {
                    const center = this.GIS.View.getCenter();
                    callback?.(center);
                };
                this.GIS.View.listeners[name] = changeCenterListener;
                this.GIS.View.olObject.on('change:center', this.GIS.View.listeners[name])
                break;
            case 'loading':
                if (this.GIS.View.listeners[name] && callback === null) {
                    this.GIS.View.olObject.un('change:center', this.GIS.View.listeners[name])
                    this.GIS.View.olObject.un('change:resolution', this.GIS.View.listeners[name])
                    this.GIS.View.olObject.un('change:rotation', this.GIS.View.listeners[name])
                    delete this.GIS.View.listeners[name]
                    return
                }

                const changeListener = () => {
                    //Loading Start
                    if (this.GIS.View.loadingStart !== null) {
                        clearTimeout(this.GIS.View.loadingStart);
                    }
                    if (this.GIS.View.firstLoading) {
                        this.GIS.View.firstLoading = false;
                        callback?.({status: 0, render: {ready: 0}});
                    } else {
                        this.GIS.View.loadingStart = setTimeout(() => {
                            callback?.({status: 0, render: {ready: 0}});
                            this.GIS.View.firstLoading = true;
                        }, this.GIS.View.delay)
                    }


                    //Loading End
                    if (this.GIS.View.loadingEnd !== null) {
                        clearTimeout(this.GIS.View.loadingEnd);
                    }
                    this.GIS.View.loadingEnd = setTimeout(() => {
                        this.setRenderCompletedCallback((render: Render) => {
                            callback?.({status: 1, render: {...render}});
                        })
                    }, this.GIS.View.delay);
                }
                this.GIS.View.listeners[name] = changeListener;
                this.GIS.View.olObject.on('change:resolution', this.GIS.View.listeners[name]);
                this.GIS.View.olObject.on('change:center', this.GIS.View.listeners[name]);
                this.GIS.View.olObject.on('change:rotation', this.GIS.View.listeners[name])
                break;
            case 'move':
                if (this.GIS.View.listeners[name] && callback === null) {
                    this.GIS.View.olObject.un('change:center', this.GIS.View.listeners[name])
                    this.GIS.View.olObject.un('change:resolution', this.GIS.View.listeners[name])
                    this.GIS.View.olObject.un('change:rotation', this.GIS.View.listeners[name])
                    delete this.GIS.View.listeners[name]
                    return
                }
                const moveListener = () => {
                    const center = this.GIS.View.getCenter();
                    const rotation = this.GIS.View.getRotation();
                    const resolution = this.GIS.View.getPointResolution();

                    callback?.({
                        center: center,
                        rotation: rotation,
                        pointResolution: resolution
                    });
                };
                this.GIS.View.listeners[name] = moveListener;
                this.GIS.View.olObject.on('change:resolution', this.GIS.View.listeners[name]);
                this.GIS.View.olObject.on('change:center', this.GIS.View.listeners[name]);
                this.GIS.View.olObject.on('change:rotation', this.GIS.View.listeners[name])
                break;
            case 'postrender':
            case 'moveend':
                if (this.listeners[name] && callback === null) {
                this.olObject!.un(name, this.listeners[name]);
                    delete this.listeners[name];
                    return;
                }

                const mapMotionListener = () => {
                    const center = this.GIS.View.getCenter();
                    const rotation = this.GIS.View.getRotation();
                    const resolution = this.GIS.View.getPointResolution();

                    callback?.({
                        center: center,
                        rotation: rotation,
                        pointResolution: resolution
                    });
                };

    this.listeners[name] = mapMotionListener;
    this.olObject!.on(name, mapMotionListener);
                break;
            case 'boundary': {
                const boundaryOptions = (options ?? {}) as BoundaryCallbackOptions;
                const boundaryCallback = callback as BoundaryCallback | null | undefined;
                const boundaryViewPort = this.olObject!.getViewport();

                // Tear down any previously registered boundary listener, whichever
                // point it was tracking (cursor pointermove or view center change).
                if (this.listeners[name]) {
                    boundaryViewPort.removeEventListener('pointermove', this.listeners[name]);
                    delete this.listeners[name];
                }
                if (this.GIS.View.listeners[name]) {
                    this.GIS.View.olObject.un('change:center', this.GIS.View.listeners[name]);
                    delete this.GIS.View.listeners[name];
                }

                if (!boundaryCallback) {
                    return;
                }

                const layerName = boundaryOptions.layerName;
                if (!layerName) {
                    throw new Error('Boundary callback requires options.layerName.');
                }

                const track = boundaryOptions.track ?? 'cursor';
                const boundaryThrottle = boundaryOptions.throttle ?? 50;
                let lastBoundaryCheck = 0;
                let lastInside: boolean | null = null;

                // Shared logic: throttle, run the point-in-boundary check and fire
                // the callback only when the inside/outside state changes.
                const evaluateBoundary = (coordinate: Coordinate) => {
                    const now = Date.now();
                    if (now - lastBoundaryCheck < boundaryThrottle) {
                        return;
                    }
                    lastBoundaryCheck = now;

                    const inside = this.GIS.Analysis.isCoordinateInsideLayer(coordinate, layerName);

                    if (inside === lastInside) {
                        return;
                    }
                    lastInside = inside;

                    let reportedCoordinate = coordinate;
                    if (boundaryOptions.epsg) {
                        reportedCoordinate = transform(coordinate, this.GIS.View.olObject.getProjection().getCode(), this.GIS.Projection.getRegistered(boundaryOptions.epsg));
                    }

                    boundaryCallback({
                        inside,
                        coordinate: reportedCoordinate
                    });
                };

                if (track === 'center') {
                    const centerListener = () => {
                        evaluateBoundary(this.GIS.View.getCenter());
                    };
                    this.GIS.View.listeners[name] = centerListener;
                    this.GIS.View.olObject.on('change:center', centerListener);
                } else {
                    const cursorListener = (event: PointerEvent) => {
                        evaluateBoundary(this.olObject!.getEventCoordinate(event));
                    };
                    this.listeners[name] = cursorListener;
                    boundaryViewPort.addEventListener('pointermove', cursorListener);
                }
                break;
            }
            default:
                throw new Error('Unknown callback type.');
        }
    }
    /**
     * Register a callback that fires when the cursor crosses the edge of a
     * boundary layer — i.e. when the inside/outside state changes as the
     * pointer moves over the map. This is the live counterpart of
     * {@link Analysis.isCoordinateInsideLayer}: the boundary polygon is held in
     * a normal vector layer (e.g. the Swedish territory for MDP), and this
     * callback lets a consumer swap coordinate tooltip labels the moment the
     * cursor enters or leaves that area.
     *
     * The callback fires only on transitions (enter → inside `true`, leave →
     * inside `false`), not on every pointer move, and carries the coordinate at
     * the transition. Pass `callback = null` to remove a previously registered
     * boundary callback.
     *
     * @param layerName - Name of the boundary layer to test against.
     * @param options - Optional `epsg` for the reported coordinate and
     *   `throttle` for the minimum interval between checks.
     * @param callback - Callback receiving `{ inside, coordinate }` on each
     *   boundary crossing, or `null` to deactivate.
     * @throws No such layer in the map.
     *
     * @example
     * GIS.Map.setBoundaryCallback('sweden_boundary', { epsg: 4326 }, ({ inside, coordinate }) => {
     *     tooltip.setLabelFormat(inside ? 'SWEREF' : 'WGS84');
     * });
     */
    public setBoundaryCallback(layerName: string, options?: BoundaryCallbackOptions, callback?: BoundaryCallback | null): void {
        const viewPort = this.olObject!.getViewport();
        const name = 'boundary';

        if (this.listeners[name]) {
            viewPort.removeEventListener('pointermove', this.listeners[name]);
            delete this.listeners[name];
        }

        if (!callback) {
            return;
        }

        const throttle = options?.throttle ?? 50;
        let lastCheck = 0;
        let lastInside: boolean | null = null;

        const pointerMoveListener = (event: PointerEvent) => {
            const now = Date.now();
            if (now - lastCheck < throttle) {
                return;
            }
            lastCheck = now;

            const coordinate = this.olObject!.getEventCoordinate(event);
            const inside = this.GIS.Analysis.isCoordinateInsideLayer(coordinate, layerName);

            if (inside === lastInside) {
                return;
            }
            lastInside = inside;

            let reportedCoordinate = coordinate;
            if (options && options.epsg) {
                reportedCoordinate = transform(coordinate, this.GIS.View.olObject.getProjection().getCode(), this.GIS.Projection.getRegistered(options.epsg));
            }

            callback({
                inside,
                coordinate: reportedCoordinate
            });
        };

        this.listeners[name] = pointerMoveListener;
        viewPort.addEventListener('pointermove', pointerMoveListener);
    }
    /**
     * Create polygon from extent method
     * @param extent - Extent array [minx, miny, maxx, maxy]
     * @param options - specify e.g. epsg, format
     * @param callback - Returns polygon representation.
     * @example GIS.Map.fromExtent([-475469.8605439196, -1110000.4781879345, -474057.3472136407, -1108587.9648576556], {sourceEPSG: 5514, targetEPSG: 3857, format: 'geojson'});
     * @returns A polygon representing the extent.
     */
    public fromExtent(extent: import("ol/extent.js").Extent, options: ProjectionTransform & ExtentOptions, callback?: GeometryString) {

        let result;
        let format;
        let geometry;
        let transformedExtent = extent;

        options && options.format ? format = options.format : format = this.GIS.Layer.format.WKT;

        if (options && options.sourceEPSG && options.targetEPSG) {
            transformedExtent = transformExtent(extent, this.GIS.Projection.getRegistered(options.sourceEPSG), this.GIS.Projection.getRegistered(options.targetEPSG));
        }

        switch (format) {
            case this.GIS.Layer.format.WKT:
                geometry = fromExtent(transformedExtent);
                result = new WKT().writeGeometry(geometry);
                break;
            case this.GIS.Layer.format.GEOJSON:
                geometry = fromExtent(transformedExtent);
                result = new GeoJSON().writeGeometry(geometry)
                break;
            default:
                throw new Error('Unknown extent format.');
        }

        if (callback) {
            callback(result);
        }

        return result;
    }
    /**
     * Synchronous retrieve of the map view. Waiting for finnish of rendering.
     * @param callback
     * @param options - additional configuration for the exported image
     * @returns Base64 representation of an image.
     */
    public exportMap(callback?: Blob, options?:Export) {
        let format = 'image/png';

        if (options && options.format) {
            format = options.format;
        }
        let elements = ['map'];
        if (options && options.elements) {
            elements = options.elements;
            if (options.elements.length === 0) {
                elements = ['map'];
            }
        }
        let mobile = false;
        if (options && options.mobile) {
            mobile = options.mobile;
        }
        this.olObject!.once('rendercomplete', () => {
            const mapCanvas:HTMLCanvasElement = document.createElement('canvas');
            const size = this.olObject!.getSize();
            mapCanvas.width = size ? size[0] : 0;
            mapCanvas.height = size ? size[1] : 0;

            const mapContext: CanvasRenderingContext2D | null = mapCanvas.getContext('2d');
            if (mapContext !== null) {
                Array.prototype.forEach.call(
                    this.olObject!.getViewport().querySelectorAll('.ol-layer canvas, .ol-layers canvas, canvas.ol-layers'),
                    function (canvas) {
                        if (canvas.width > 0) {
                            const opacity =
                                canvas.parentNode.style.opacity || canvas.style.opacity;
                            mapContext.globalAlpha = opacity === '' ? 1 : Number(opacity);

                            const backgroundColor = canvas.parentNode.style.backgroundColor;
                            if (backgroundColor) {
                                mapContext.fillStyle = backgroundColor;
                                mapContext.fillRect(0, 0, canvas.width, canvas.height);
                            }

                            let matrix;
                            const transform = canvas.style.transform;
                            if (transform) {
                                // Get the transform parameters from the style's transform matrix
                                matrix = transform
                                    .match(/^matrix\(([^\(]*)\)$/)[1]
                                    .split(',')
                                    .map(Number);
                            } else {
                                matrix = [
                                    parseFloat(canvas.style.width) / canvas.width,
                                    0,
                                    0,
                                    parseFloat(canvas.style.height) / canvas.height,
                                    0,
                                    0,
                                ];
                            }
                            // Apply the transform to the export map context
                            CanvasRenderingContext2D.prototype.setTransform.apply(
                                mapContext,
                                matrix
                            );
                            mapContext.drawImage(canvas, 0, 0);
                        }
                    }
                );
                mapContext.globalAlpha = 1;
                this.exportCartographyElement(format, mapContext, mapCanvas, elements, mobile, callback)
            }
        });
        this.olObject!.renderSync();
    }
    /**
     * Get the pixel for a coordinate. This takes a coordinate in the user projection and returns the corresponding pixel.
     * See {@link https://openlayers.org/en/latest/apidoc/module-ol_Map-Map.html#getPixelFromCoordinate}
     * @param coordinate Map projection coordinates as real numbers.
     * @param callback
     */
    public getPixelFromCoordinate(coordinate:Coordinate, callback?:XY) {

        const coordinates = this.olObject!.getPixelFromCoordinate(coordinate);

        if (callback) {
            callback(coordinates[0], coordinates[1]);
        }

        return [coordinates[0], coordinates[1]];
    }
    /**
     * Recreates all interactions from the map.
     * Originally it is a fix for Safari 13 which kills pointerup event.
     * All interactions are created from the scratch.
     * Be aware or breaking some ongoing actions on the map.
     * Doesn't care if interaction is active or not!
     * @returns Interaction collections.
     * @throws Instance of the interaction cannot be recreated.
     */
    public resetInteractions() {
        const collection:Interaction[] = [];
        const names:string[] = [];
        this.olObject!.getInteractions().forEach(interaction => {
            collection.push(interaction);
        });

        collection.forEach(interaction => {

            if (interaction instanceof Identify) {
                this.GIS.Interaction.entries.map((entry:Interaction) => {
                    if (entry instanceof Identify) {
                        this.olObject!.removeInteraction(interaction);
                        this.olObject!.addInteraction(interaction);
                        names.push("Identify");
                    }
                })
            } else if (interaction instanceof Select) {
                this.olObject!.removeInteraction(interaction);
                this.olObject!.addInteraction(interaction);
                names.push(interaction.get('name'));
            } else if (interaction instanceof DragPan) {
                this.olObject!.removeInteraction(interaction);
                this.olObject!.addInteraction(new DragPan);
                names.push("DragPan");
            } else if (interaction instanceof DoubleClickZoom) {
                this.olObject!.removeInteraction(interaction);
                this.olObject!.addInteraction(new DoubleClickZoom);
                names.push("DoubleClickZoom");
            } else if (interaction instanceof PinchRotate) {
                this.olObject!.removeInteraction(interaction);
                this.olObject!.addInteraction(new PinchRotate);
                names.push("PinchRotate");
            } else if (interaction instanceof PinchZoom) {
                this.olObject!.removeInteraction(interaction);
                this.olObject!.addInteraction(new PinchZoom);
                names.push("PinchZoom");
            } else if (interaction instanceof KeyboardPan) {
                this.olObject!.removeInteraction(interaction);
                this.olObject!.addInteraction(new KeyboardPan);
                names.push("KeyboardPan");
            } else if (interaction instanceof KeyboardZoom) {
                this.olObject!.removeInteraction(interaction);
                this.olObject!.addInteraction(new KeyboardZoom);
                names.push("KeyboardZoom");
            } else if (interaction instanceof MouseWheelZoom) {
                this.olObject!.removeInteraction(interaction);
                this.olObject!.addInteraction(new MouseWheelZoom);
                names.push("MouseWheelZoom");
            } else if (interaction instanceof DragZoom) {
                this.olObject!.removeInteraction(interaction);
                this.olObject!.addInteraction(new DragZoom);
                names.push("DragZoom");
            } else if (interaction instanceof DragRotate) {
                this.olObject!.removeInteraction(interaction);
                this.olObject!.addInteraction(new DragRotate);
                names.push("DragRotate");
            } else if (interaction instanceof DragAndDrop) {
                this.olObject!.removeInteraction(interaction);
                this.olObject!.addInteraction(new DragAndDrop);
                names.push("DragAndDrop");
            } else if (interaction instanceof Draw) {
                console.warn('You are currently in the middle of drawing. Draw interaction will be not recreated.')
            } else if (interaction instanceof Hover) {
                this.olObject!.removeInteraction(interaction);
                this.olObject!.addInteraction(interaction);
                names.push("Hover");
            } else {
                // Skip unrecognized interactions (e.g. Modify, Snap) — they are managed elsewhere
                console.warn(`[Map.resetInteractions] Skipping unrecognized interaction: ${interaction.constructor.name}`);
            }
        });
        return names;
    }

    /**
     * Render completed callback, it will be called when the map is rendered.
     *
     * `ready` is 1 once nothing is pending any more. Requests the map gave up on
     * are reported separately as `raster_unfetched` / `vector_unfetched` and do
     * not hold `ready` at 0 — they are not going to resolve on their own, and
     * {@link Layer.fetchFailed} re-requests them.
     *
     * @param callback
     */
    public setRenderCompletedCallback(callback?: Renderer) {
        const callbackWithStatus = () => {
            const response: Render = this.GIS.Layer.getNumberOfFailed();
            response.raster_failed && response.raster_failed > 0 || response.vector_failed && response.vector_failed > 0 ? response['ready'] = 0 : response['ready'] = 1;
            callback?.(response);
        }

        if (this.olObject) {
            this.olObject!.once('rendercomplete', callbackWithStatus);
            this.olObject!.renderSync();
        }

    }

    /**
     * Get the extent for the given coordinates.
     * @param coordinates
     */
    public getExtentFromCoordinates(coordinates: number[] | number[][] | number[][][]): Extent {
        return getExtentFromCoordinates(coordinates);
    }

    private exportCartographyElement(format: string, mapContext:CanvasRenderingContext2D, mapCanvas:HTMLCanvasElement, elements:string[], mobile:boolean, callback?: Blob) {
        try {
            const isScaleBarExist = document.querySelector(".ol-scale-bar.ol-unselectable");
            const isScaleLineExist = document.querySelector(".ol-scale-line.ol-unselectable");
            const odd = document.querySelectorAll(".ol-scale-singlebar-odd");
            const even = document.querySelectorAll(".ol-scale-singlebar-even");
            const steptexts = document.querySelectorAll(".ol-scale-step-text");
            const markers = document.querySelectorAll(".ol-scale-step-marker");

            let scaleElement: Element = document.createElement("div");
            let fontSizeBackup: string;
            let scaleParameter: number = 1;
            let scaleBackgroundColor: string = "rgba(255,255,255,0.7)";

            let parameterString = {backgroundColor: null, scale: scaleParameter}
            let heightOffset = 20;
            if (isScaleBarExist !== null) {
                scaleElement = isScaleBarExist;
                scaleParameter = 1;
                parameterString = {backgroundColor: null, scale: scaleParameter}
                if (mobile) {
                    markers.forEach(marker => {
                        marker.classList.add('ol-scale-step-marker-zoomed');
                    });
                    steptexts.forEach(steptext => {
                        steptext.classList.add('ol-scale-step-text-zoomed');
                    });
                    even.forEach(evenChild => {
                        evenChild.classList.add('ol-scale-singlebar-even-zoomed');
                    });
                    odd.forEach(oddChild => {
                        oddChild.classList.add('ol-scale-singlebar-odd-zoomed');
                    });
                    (document.querySelector(".ol-scale-text") as HTMLElement).classList.add('ol-scale-text-zoomed');
                    (document.querySelector(".ol-scale-bar") as HTMLElement).style.backgroundColor = scaleBackgroundColor;
                    (document.querySelector(".ol-scale-bar") as HTMLElement).style.border = "8px solid rgba(255,255,255,0)";
                    (document.querySelector(".ol-scale-bar") as HTMLElement).style.border = ("8px solid " + "scaleBackgroundColor");
                    heightOffset = 60;
                    scaleParameter = 2;
                    parameterString = {backgroundColor: null, scale: scaleParameter};
                }
            } else if (isScaleLineExist !== null) {
                heightOffset = 20;
                scaleParameter = 1;
                parameterString = {backgroundColor: null, scale: scaleParameter};
                scaleElement = isScaleLineExist;
                fontSizeBackup = (document.querySelector(".ol-scale-line-inner") as HTMLElement).style.fontSize;
                if (mobile) {
                    (document.querySelector(".ol-scale-line-inner") as HTMLElement).style.fontSize = "1.2rem";
                    scaleParameter = 2;
                    parameterString = {backgroundColor: null, scale: scaleParameter};
                    heightOffset = 50;
                }
            } else {
                console.error("scale element does not exist");
            }

            html2canvas(scaleElement as HTMLElement, parameterString).then(function (scaleCanvas: HTMLCanvasElement) {
                const scalePositionX = parseFloat(getComputedStyle(scaleElement, null).left);
                const scaleLineBottom = parseFloat(getComputedStyle(scaleElement).bottom);
                const scaleLineHeight = parseFloat(getComputedStyle(scaleElement).height);
                const sceneHeight = ((document.querySelector(".ol-unselectable.ol-layers  canvas") as HTMLCanvasElement).height);
                const scalePositionY = sceneHeight - (scaleLineBottom + scaleLineHeight) - heightOffset;
                let blob;

                if (elements.includes('map') && elements.includes('scale')) {
                    mapContext.drawImage(scaleCanvas, scalePositionX, scalePositionY, scaleCanvas.width, scaleCanvas.height);
                    blob = mapCanvas.toDataURL(format);
                } else if (elements.includes('scale')) {
                    blob = scaleCanvas.toDataURL(format);
                } else if (elements.includes('map')) {
                    blob = mapCanvas.toDataURL(format);
                } else {
                    throw {name: "illegal value ", message: "Inserted value is not allowed."}
                }

                if (callback) {
                    callback(blob);
                }

                if (isScaleBarExist !== null) {
                    scaleElement = isScaleBarExist;

                    if (mobile === true) {
                        markers.forEach(marker => {
                            marker.classList.remove('ol-scale-step-marker-zoomed');
                        });
                        steptexts.forEach(steptext => {
                            steptext.classList.remove('ol-scale-step-text-zoomed');
                        });
                        even.forEach(evenChild => {
                            evenChild.classList.remove('ol-scale-singlebar-even-zoomed');
                        });
                        odd.forEach(oddChild => {
                            oddChild.classList.remove('ol-scale-singlebar-odd-zoomed');
                        });
                        (document.querySelector(".ol-scale-text") as HTMLElement).classList.remove('ol-scale-text-zoomed');
                        (document.querySelector(".ol-scale-bar") as HTMLElement).style.backgroundColor = "rgba(255,255,255,0)";
                        (document.querySelector(".ol-scale-bar") as HTMLElement).style.border = "8px solid rgba(255,255,255,0)";
                    }
                } else if (isScaleLineExist !== null) {
                    scaleElement = isScaleLineExist;
                    if (mobile === true) {
                        (document.querySelector(".ol-scale-line-inner") as HTMLElement).style.fontSize = fontSizeBackup;
                    }
                } else {
                    throw {name: "scale element doesn't exist", message: "scale element doesn't exist"};
                }
                return blob;
            });
        } catch (error: unknown) {
            if (error instanceof Error) {
                console && console.warn(error.message);
            }
        }
    }
}

export default Map;